import { suspend } from "suspend-react";
import {
  DefaultLoadingManager,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { Settings8thWall } from "../CanvasContentComponent";
import { three8thPipeline } from "../pipelines/three8thPipeline";
import { imageTrackingPipeline } from "../pipelines/imageTrackingPipeline";
import { realtimeReflectionsPipeline } from "../pipelines/realtimeReflectionsPipeline";
import { bloomPipeline } from "../pipelines/bloomPipeline";
import { FxProps, TrackingSettings8thWall } from "../CanvasComponent";
import { createImageTargetStates } from "../utils/imageTargetManager";
import { three8thFacePipeline } from "../pipelines/three8thFacePipeline";
import { useStore } from "../store/Store";
import { ACTION_TRIGGERS } from "../ActionTriggers";

const key = process.env.REACT_APP_8TH_KEY || "";
const url8thWall = `//apps.8thwall.com/xrweb?appKey=${key}`;

export type ImageTrackingProps = {
  imageTargets?: Array<string> | null;
  foundTarget: Function;
  lostTarget: Function;
};

export type Props8thWall = {
  enabled: boolean;
  tracking: TrackingSettings8thWall;
  camera: PerspectiveCamera;
  scene: Scene;
  renderer: WebGLRenderer;
  settings: Settings8thWall;
  imageTrackingProps: ImageTrackingProps | null;
  fxProps?: FxProps;
};

const loadScript = (src: string) =>
  new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.addEventListener("load", resolve);
    script.addEventListener("error", reject);
    script.src = src;
    document.body.appendChild(script);
  });

const loadXR8 = async () => {
  const src = url8thWall;
  await loadScript(src);

  await new Promise((resolve: any) => {
    if ((window as any).XR8) resolve();
    else window.addEventListener("xrloaded", resolve);
  });

  return (window as any).XR8 || null;
};

const startupPipeline = (
  xr8: any,
  scene: Scene,
  camera: PerspectiveCamera,
  renderer: WebGLRenderer,
  tracking: TrackingSettings8thWall,
  imageTrackingProps: ImageTrackingProps | null,
  setTrackingReady: (ready?: boolean) => void,
  fxProps?: FxProps
) => {
  if (!xr8 || !scene || !camera || !renderer) return;
  // create pipelines
  const pipelines = [];
  const triggerAction = useStore.getState().triggerAction;

  // Check tracking modes
  const enabledSurface =
    tracking === "only-surface" || tracking === "image-surface";
  const enabledImage =
    tracking === "only-image" || tracking === "image-surface";
  const enableFace = tracking === "only-face";

  console.log(
    "surface",
    enabledSurface,
    " image",
    enabledImage,
    "face",
    enableFace
  );

  xr8.XrController.configure({ disableWorldTracking: !enabledSurface }); // Enables SLAM

  if (fxProps?.realtimeReflections && !enableFace) {
    pipelines.push(realtimeReflectionsPipeline(xr8, scene, camera, renderer));
  } else {
    pipelines.push(xr8.GlTextureRenderer.pipelineModule());
  }

  if (enableFace) {
    pipelines.push(xr8.FaceController.pipelineModule());

    xr8.FaceController.configure({
      meshGeometry: [
        xr8.FaceController.MeshGeometry.FACE,
        // In case we need more information about the mesh we can get additional meshes
        // XR8.FaceController.MeshGeometry.EYES,
        // XR8.FaceController.MeshGeometry.MOUTH,
      ],
      coordinates: { mirroredDisplay: true },
    });

    pipelines.push(
      three8thFacePipeline({
        xr8,
        scene,
        camera,
        renderer,
        onTrackingReady: () => {
          setTrackingReady(true);
        },
        onFaceFound: (detail: any) => {
          triggerAction(
            ACTION_TRIGGERS.FACE_FOUND,
            "canvas",
            undefined,
            detail
          );
        },
        onFaceLost: () => {
          triggerAction(ACTION_TRIGGERS.FACE_LOST, "canvas");
        },
      })
    );
    // Bloom pipeline renders scene and camera. Be aware that the scene is also rendered in the three8thPipeline
    fxProps?.bloom?.enabled &&
      pipelines.push(
        bloomPipeline({
          xr8,
          scene,
          camera,
          renderer,
          bloomProps: fxProps?.bloom,
        })
      );
  } else {
    console.log("build 8thPipeline");
    pipelines.push(xr8.XrController.pipelineModule());

    pipelines.push(
      three8thPipeline(
        {
          xr8,
          scene,
          camera,
          renderer,
          initialCameraPosition: new Vector3(0, 2, 4.5),
        },
        setTrackingReady
      )
    );
    // Bloom pipeline renders scene and camera. Be aware that the scene is also rendered in the three8thPipeline
    fxProps?.bloom?.enabled &&
      pipelines.push(
        bloomPipeline({
          xr8,
          scene,
          camera,
          renderer,
          bloomProps: fxProps?.bloom,
        })
      );
  }

  if (enabledImage && imageTrackingProps) {
    createImageTargetStates(imageTrackingProps!.imageTargets as string[]);
    pipelines.push(imageTrackingPipeline(xr8, imageTrackingProps));
  }

  xr8.clearCameraPipelineModules();
  xr8.addCameraPipelineModules(pipelines);
  xr8.run({
    canvas: renderer.domElement,
    cameraConfig: {
      direction: enableFace
        ? xr8.XrConfig.camera().FRONT
        : xr8.XrConfig.camera().BACK,
    },
    allowedDevices: enableFace
      ? xr8.XrConfig.device().ANY
      : xr8.XrConfig.device().MOBILE,
  });
  return () => {
    xr8.clearCameraPipelineModules();
    xr8.stop();
    console.log("stopped");
  };
};

export const use8thWall = ({
  scene,
  camera,
  renderer,
  imageTrackingProps,
  tracking,
  enabled,
  fxProps,
}: Props8thWall) => {
  if (!enabled) return;
  const loadingManager = DefaultLoadingManager;

  const xr8 = suspend(async () => {
    loadingManager.itemStart(url8thWall);
    try {
      const _xr8 = await loadXR8();
      startupPipeline(
        _xr8,
        scene,
        camera,
        renderer,
        tracking,
        imageTrackingProps,
        (ready?: boolean) => {
          // tracking ready
          loadingManager.itemEnd(url8thWall);
          return _xr8;
        },
        fxProps
      );
      // return _xr8;
    } catch (err) {
      loadingManager.itemError(url8thWall);
      loadingManager.itemEnd(url8thWall);
    }
  }, ["8thWall"]);

  return xr8;
};
